import React from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  CircularProgress,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { createStudentDelay, fetchReservaStudentIds } from "../../api/ticketingApi";
import { useClientes } from "../../hooks/useClientes";

const TEAL = "#21a598";
const TEAL_DARK = "#1b887f";

type Props = {
  reservaId: number;
  /** Si lo pasas, no se mostrará selector y se enviará directamente */
  studentId?: number;
  buttonText?: string;
  defaultMinutes?: number;
  onSuccess?: (ticketId: string) => void;
  variant?: "text" | "outlined" | "contained";
  size?: "small" | "medium" | "large";
};



export default function StudentDelayButton({
  reservaId,
  studentId,
  buttonText = "",
  defaultMinutes = 10,
  onSuccess,
  variant = "outlined",
  size = "medium",
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [minutesLate, setMinutesLate] = React.useState<number>(defaultMinutes);
  const [comment, setComment] = React.useState("");
  const [files, setFiles] = React.useState<File[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [student, setStudent] = React.useState<number | "">("");
  const [studentIds, setStudentIds] = React.useState<number[]>([]);
  const [snack, setSnack] = React.useState<{
    open: boolean;
    msg: string;
    sev: "success" | "error";
  }>({ open: false, msg: "", sev: "success" });

  // Mapeo id -> nombre del alumno
  const { rows: clientes } = useClientes();
  const alumnoLabel = (id?: number) => {
    if (!id) return "";
    const cli = clientes.find((c) => c.id === id);
    return cli?.nombreCompleto || `Alumno #${id}`;
  };

  const needSelect = studentId == null;

  React.useEffect(() => {
    if (open && needSelect) {
      fetchReservaStudentIds(reservaId)
        .then((ids) => setStudentIds(ids))
        .catch(() => setStudentIds([]));
    }
  }, [open, needSelect, reservaId]);

  const onChooseFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setFiles(Array.from(e.target.files));
  };

  const submit = async () => {
    const sid = needSelect ? Number(student) : Number(studentId);
    if (!sid) {
      setSnack({ open: true, msg: "Selecciona un alumno", sev: "error" });
      return;
    }
    if (!minutesLate || minutesLate < 1) {
      setSnack({ open: true, msg: "Los minutos deben ser ≥ 1", sev: "error" });
      return;
    }

    setLoading(true);
    try {
      const res = await createStudentDelay(reservaId, sid, minutesLate, comment, files);
      const ticketId = res.id || res.ticketId || "";
      setSnack({
        open: true,
        msg: res.dedup ? `Adjuntado al ticket ${ticketId}` : `Ticket creado: ${ticketId}`,
        sev: "success",
      });
      onSuccess?.(ticketId);
      setOpen(false);
      setComment("");
      setFiles([]);
      setStudent("");
      setMinutesLate(defaultMinutes);
    } catch (e: any) {
      setSnack({
        open: true,
        msg: e?.response?.data?.message || "Error creando el ticket",
        sev: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedName = !needSelect ? alumnoLabel(studentId) : "";

  return (
    <>
      <Button
        startIcon={<AccessTimeIcon />}
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        title="Retraso del alumno"
        sx={{
          minWidth: 0,
          px: 1.25,
          py: 0.8,
          pl: 1.5,
          fontSize: "0.8125rem",
          textTransform: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#21a598",
          borderColor: "#21a598",
          "&:hover": {
            backgroundColor: "rgba(33,165,152,0.08)",
            borderColor: "#1b887f",
          },
          "& .MuiButton-startIcon": {
            margin: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "18px", 
            height: "18px",
            "& svg": {
              width: "18px", 
              height: "18px",
              display: "block",
              margin: "0 auto",
              color: "#21a598",
              transform: "scale(1.2)", 
              transformOrigin: "center",
            },
          },
        }}
      >
        {buttonText}
      </Button>

      <Dialog open={open} onClose={() => !loading && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {`Reportar retraso del/a alumno/a${selectedName ? ` — ${selectedName}` : ""}`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {needSelect && (
              <FormControl fullWidth>
                <InputLabel id="student-id-label">Alumno/a</InputLabel>
                <Select
                  labelId="student-id-label"
                  label="Alumno/a"
                  value={student}
                  onChange={(e) => setStudent(e.target.value as number)}
                >
                  {studentIds.map((id) => (
                    <MenuItem key={id} value={id}>
                      {alumnoLabel(id)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {!needSelect && (
              <TextField
                label="Alumno/a"
                value={alumnoLabel(studentId)}
                InputProps={{ readOnly: true }}
                fullWidth
              />
            )}

            <TextField
              label="Minutos de retraso"
              type="number"
              value={minutesLate}
              onChange={(e) => setMinutesLate(Number(e.target.value))}
              inputProps={{ min: 1 }}
              fullWidth
            />
            <TextField
              label="Comentario (opcional)"
              multiline
              minRows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              fullWidth
            />
            <Button component="label" variant="text">
              Adjuntar archivos
              <input hidden multiple type="file" onChange={onChooseFiles} />
            </Button>
            {files.length > 0 && <small>{files.length} archivo(s) seleccionados</small>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={submit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Crear ticket"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.sev} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
